import React, { useEffect } from "react";
import { useFormikContext, ErrorMessage } from "formik";
import { dspDropdownOptions } from "../../Data/dropdownOptions";
import { getSubgenreOptions } from "../../utils/selectUtils";
import MultiSelect from "../MultiSelect/MultiSelect";
import { DSPPlatform, DropdownOptions } from '../../types/dropdownTypes';
import { PitchFormValues } from "../Schema/pitchValidationSchema";
import { RequiredAsterisk } from "../../../components/common/RequiredAsterisk";
import { SimilarArtist } from "./SimilarArtist";
import InfoTooltip from "../../../components/common/InfoTooltip";
import { classifyCompanyType } from "../../../utils/companyTypeClassification";


interface DspSpecificInfoProps {
  selectedDSPs: {
    spotify: boolean;
    amazon: boolean;
    deezer: boolean;
    TIDAL: boolean;
    audiomack: boolean;
    soundcloud: boolean;
    apple: boolean;
    youtube: boolean;
    siriusxm: boolean;
    vevo: boolean;
  };
  onDownloadAppleCsv?: () => void;
  companyType?: string;
}

/**
 * Whether any selected platform has fields in this section. Email has none, so
 * with no DSP (or only Email) selected the section would render as an empty box.
 */
export const hasDspSpecificFields = (selectedDSPs: object): boolean =>
  Object.entries(selectedDSPs).some(([dsp, selected]) => dsp !== "email" && selected === true);

const DspSpecificInfo: React.FC<DspSpecificInfoProps> = ({ selectedDSPs, onDownloadAppleCsv, companyType }) => {
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext<PitchFormValues>();
  const isDspSelected = (dsp: keyof typeof selectedDSPs): boolean =>
    selectedDSPs[dsp];
  const companyTypeCategory = classifyCompanyType(companyType);

  // Check if any of the DSPs that share Release Stage are selected
  const releaseStageSelectedDSPs = () => {
    const dsps = [];
    if (isDspSelected("soundcloud")) dsps.push("soundcloud");
    if (isDspSelected("youtube")) dsps.push("youtube");
    if (isDspSelected("apple")) dsps.push("apple");
    return dsps;
  };

  const isMultipleReleaseStageSelected = () => {
    return releaseStageSelectedDSPs().length > 1;
  };

  // Check if any of the DSPs that share Label are selected (SoundCloud, Apple)
  const labelSelectedDSPs = () => {
    const dsps = [];
    if (isDspSelected("soundcloud")) dsps.push("soundcloud");
    if (isDspSelected("apple")) dsps.push("apple");
    return dsps;
  };

  const isMultipleLabelSelected = () => {
    return labelSelectedDSPs().length > 1;
  };

  // Check if any of the DSPs that share Promotion Date are selected (YouTube, Apple)
  const promotionDateSelectedDSPs = () => {
    const dsps = [];
    if (isDspSelected("youtube")) dsps.push("youtube");
    if (isDspSelected("apple")) dsps.push("apple");
    return dsps;
  };

  const isMultiplePromotionDateSelected = () => {
    return promotionDateSelectedDSPs().length > 1;
  };

  useEffect(() => {
    // Sync release stage values when multiple DSPs are selected
    const dsps = releaseStageSelectedDSPs();
    if (dsps.length > 1) {
      // Find the first non-empty value
      const existingValue = values.soundCloudReleaseStage || values.youtubeReleaseStage || values.appleReleaseStage;
      if (existingValue) {
        if (dsps.includes("soundcloud") && !values.soundCloudReleaseStage) {
          setFieldValue("soundCloudReleaseStage", existingValue);
        }
        if (dsps.includes("youtube") && !values.youtubeReleaseStage) {
          setFieldValue("youtubeReleaseStage", existingValue);
        }
        if (dsps.includes("apple") && !values.appleReleaseStage) {
          setFieldValue("appleReleaseStage", existingValue);
        }
      }
    }
  }, [selectedDSPs.youtube, selectedDSPs.soundcloud, selectedDSPs.apple, values.youtubeReleaseStage, values.soundCloudReleaseStage, values.appleReleaseStage]);

  useEffect(() => {
    // Sync label values when multiple DSPs are selected (SoundCloud, Apple)
    const dsps = labelSelectedDSPs();
    if (dsps.length > 1) {
      const existingValue = values.soundCloudLabel || values.appleLabel;
      if (existingValue) {
        if (dsps.includes("soundcloud") && !values.soundCloudLabel) {
          setFieldValue("soundCloudLabel", existingValue);
        }
        if (dsps.includes("apple") && !values.appleLabel) {
          setFieldValue("appleLabel", existingValue);
        }
      }
    }
  }, [selectedDSPs.soundcloud, selectedDSPs.apple, values.soundCloudLabel, values.appleLabel]);

  useEffect(() => {
    // Sync promotion date values when multiple DSPs are selected (YouTube, Apple)
    const dsps = promotionDateSelectedDSPs();
    if (dsps.length > 1) {
      const existingValue = values.youtubePromotionDate || values.applePromotionDate;
      if (existingValue) {
        if (dsps.includes("youtube") && !values.youtubePromotionDate) {
          setFieldValue("youtubePromotionDate", existingValue);
        }
        if (dsps.includes("apple") && !values.applePromotionDate) {
          setFieldValue("applePromotionDate", existingValue);
        }
      }
    }
  }, [selectedDSPs.youtube, selectedDSPs.apple, values.youtubePromotionDate, values.applePromotionDate]);

  // Helper function to render select options
  const renderOptions = (options: string[]) => {
    return options.map((option) => (
      <option key={option} value={option.toLowerCase().replace(/\s+/g, "-")}>
        {option}
      </option>
    ));
  };

  const getSubgenres = (dsp: DSPPlatform): string[] => {
    return getSubgenreOptions(dsp, values.mainGenre || []);
  };

  // Helper function to get Deezer playlists filtered by main genre
  const getDeezerPlaylists = (): string[] => {
    const mainGenres = values.mainGenre || [];
    if (mainGenres.length === 0) return [];

    // Get all playlists for the selected genres and deduplicate
    const allPlaylists = mainGenres.flatMap(
      (genre) => dspDropdownOptions.deezerPlaylists[genre as keyof typeof dspDropdownOptions.deezerPlaylists] || []
    );

    // Remove duplicates and sort
    return Array.from(new Set(allPlaylists)).sort();
  };

  // Helper function to get Spotify playlists filtered by main genre
  const getSpotifyPlaylists = (): string[] => {
    const mainGenres = values.mainGenre || [];
    if (mainGenres.length === 0) return [];

    // Get all playlists for the selected genres and deduplicate
    const allPlaylists = mainGenres.flatMap(
      (genre) => dspDropdownOptions.spotifyPlaylists[genre as keyof typeof dspDropdownOptions.spotifyPlaylists] || []
    );

    // Remove duplicates and sort
    return Array.from(new Set(allPlaylists)).sort();
  };

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFieldValue(field, file);
    }
  };

  // Add YouTube dropdown options

  return (
    <section className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-6" data-tour="dsp-specific-info-section">
      <h2 className="mb-6 text-xl font-semibold text-black">DSP-Specific Information</h2>

      {/* Checkboxes Row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isDspSelected("spotify") && (
          <label
            htmlFor="spotify-playlist"
            className="flex min-h-[72px] items-center gap-3 p-4 transition-colors bg-white border cursor-pointer rounded-xl border-neutral-200 hover:border-primary-300 hover:bg-primary-50"
          >
            <input
              type="checkbox"
              id="spotify-playlist"
              name="pinToThisIsPlaylist"
              checked={values.pinToThisIsPlaylist || false}
              onChange={(e) => setFieldValue("pinToThisIsPlaylist", e.target.checked)}
              className="w-5 h-5 rounded shrink-0 cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
            />
            <span className="text-sm font-medium leading-snug text-neutral-800">
              Pin to "This Is" Playlist on Spotify
            </span>
          </label>
        )}

        {isDspSelected("spotify") && (
          <label
            htmlFor="pitch-to-notable"
            className="flex min-h-[72px] items-center gap-3 p-4 transition-colors bg-white border cursor-pointer rounded-xl border-neutral-200 hover:border-primary-300 hover:bg-primary-50"
          >
            <input
              type="checkbox"
              id="pitch-to-notable"
              name="pitchToNotable"
              checked={values.pitchToNotable || false}
              onChange={(e) => setFieldValue("pitchToNotable", e.target.checked)}
              className="w-5 h-5 rounded shrink-0 cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
            />
            <span className="flex items-center gap-1 text-sm font-medium leading-snug text-neutral-800">
              Pitch Songwriter to Notable
              <InfoTooltip content="Pitch your primary songwriters here. This is only for publishing" />
            </span>
          </label>
        )}

        
        {isDspSelected("audiomack") && (
          <label
            htmlFor="audiomack-trending"
            className="flex min-h-[72px] items-center gap-3 p-4 transition-colors bg-white border cursor-pointer rounded-xl border-neutral-200 hover:border-primary-300 hover:bg-primary-50"
          >
            <input
              type="checkbox"
              id="audiomack-trending"
              name="pitchToAudiomackTrending"
              checked={values.pitchToAudiomackTrending || false}
              onChange={(e) => setFieldValue("pitchToAudiomackTrending", e.target.checked)}
              className="w-5 h-5 rounded shrink-0 cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
            />
            <span className="text-sm font-medium leading-snug text-neutral-800">
              Pitch to Audiomack Trending
            </span>
          </label>
        )}

        {isDspSelected("apple") && onDownloadAppleCsv && (() => {
          const isAppleValid =
            !!values.appleId &&
            !!values.appleAlbumId &&
            Array.isArray(values.appleSubgenre) && values.appleSubgenre.length > 0 &&
            !!values.applePitchFrom &&
            !!values.applePitchTo;
          return (
            <button
              type="button"
              onClick={onDownloadAppleCsv}
              disabled={!isAppleValid}
              title={!isAppleValid ? "Fill in all required Apple Music fields first" : undefined}
              className={`flex min-h-[72px] items-center p-4 bg-white rounded-xl border shadow-sm transition-all gap-3 text-left w-full ${
                isAppleValid
                  ? "border-neutral-300 hover:shadow-md cursor-pointer"
                  : "border-neutral-200 opacity-50 cursor-not-allowed"
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mt-0.5 shrink-0 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
              </svg>
              <span className="text-sm font-medium leading-snug text-neutral-800">
                Download Apple Music CSV
              </span>
            </button>
          );
        })()}

        {isDspSelected("soundcloud") && (
          <label
            htmlFor="is-premier-program"
            className="flex min-h-[72px] items-center gap-3 p-4 transition-colors bg-white border cursor-pointer rounded-xl border-neutral-200 hover:border-primary-300 hover:bg-primary-50"
          >
            <span className="flex items-start pt-1" style={{ minWidth: 24 }}>
              <input
                type="checkbox"
                id="is-premier-program"
                name="isPremierProgram"
                checked={values.isPremierProgram || false}
                onChange={(e) => setFieldValue("isPremierProgram", e.target.checked)}
                className="w-5 h-5 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
              />
            </span>
            <span className="flex-1 text-sm font-medium leading-snug text-neutral-800">
              Are you part of SoundCloud's Premier program?
            </span>
          </label>
        )}
      </div>


      {/* Subgenre selection for each platform */}
      <div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {isDspSelected("spotify") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-neutral-700"
                htmlFor="spotify-subgenre"
              >
                Spotify Subgenre <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("spotify")}
                value={values.spotifySubgenre || []}
                onChange={(value) => { setFieldValue("spotifySubgenre", value); setFieldTouched("spotifySubgenre", true, false); }}
                placeholder="Select 2-3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="spotifySubgenre" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {isDspSelected("amazon") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-neutral-700"
                htmlFor="amazon-subgenre"
              >
                Amazon Subgenre <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("amazon")}
                value={values.amazonSubgenre || []}
                onChange={(value) => { setFieldValue("amazonSubgenre", value); setFieldTouched("amazonSubgenre", true, false); }}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="amazonSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {isDspSelected("deezer") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-neutral-700"
                htmlFor="deezer-subgenre"
              >
                Deezer Subgenre <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("deezer")}
                value={values.deezerSubgenre || []}
                onChange={(value) => { setFieldValue("deezerSubgenre", value); setFieldTouched("deezerSubgenre", true, false); }}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="deezerSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {isDspSelected("audiomack") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-neutral-700"
                htmlFor="audiomack-subgenre"
              >
                Audiomack Subgenre  <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("audiomack")}
                value={values.audiomackSubgenre || []}
                onChange={(value) => { setFieldValue("audiomackSubgenre", value); setFieldTouched("audiomackSubgenre", true, false); }}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="audiomackSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}
          {isDspSelected("TIDAL") && (
            <div>
              <label
                className="block mt-4 mb-2 text-sm font-medium text-neutral-900"
                htmlFor="TIDAL-subgenre"
              >
                TIDAL Subgenre  <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("TIDAL")}
                value={values.TIDALSubgenre || []}
                onChange={(value) => { setFieldValue("TIDALSubgenre", value); setFieldTouched("TIDALSubgenre", true, false); }}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="TIDALSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {isDspSelected("soundcloud") && (
            <div>
              <label
                className="block mt-4 mb-2 text-sm font-medium text-neutral-900"
                htmlFor="soundcloud-subgenre"
              >
                SoundCloud Subgenre   <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("soundcloud")}
                value={values.soundCloudSubgenre || []}
                onChange={(value) => { setFieldValue("soundCloudSubgenre", value); setFieldTouched("soundCloudSubgenre", true, false); }}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="soundCloudSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}
        </div>


      </div>

      {/* Spotify Specific Fields */}
      {isDspSelected("spotify") && (
        <div className="mt-2">
          <div className="grid grid-cols-2 gap-x-4">
            <div className="flex flex-col mb-4">
              <label
                className="flex items-center gap-1 mt-4 mb-2 text-sm font-medium text-neutral-700"
                htmlFor="hometown"
              >
                Artist Hometown <RequiredAsterisk />
                <InfoTooltip content="Spotify   <br> 
Artist's place of origin, used for regional playlist consideration and cultural context.
"   />
              </label>
              <input
                type="text"
                id="hometown"
                name="artistHometown"
                placeholder="Enter Hometown"
                value={values.artistHometown || ""}
                onChange={(e) => setFieldValue("artistHometown", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.artistHometown && touched.artistHometown ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              />
              <ErrorMessage name="artistHometown" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
            <div className="flex flex-col mb-4">
              <label
                className="flex items-center gap-1 mt-4 mb-2 text-sm font-medium text-neutral-700"
                htmlFor="current-town"
              >
                Artist Current Town  <RequiredAsterisk />
                <InfoTooltip content={`Spotify <br> Where the artist currently resides, helping inform geographic targeting and local scene connections.`} />

              </label>
              <input
                type="text"
                id="current-town"
                name="artistCurrentTown"
                placeholder="Enter Current Town"
                value={values.artistCurrentTown || ""}
                onChange={(e) => setFieldValue("artistCurrentTown", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.artistCurrentTown && touched.artistCurrentTown ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              />
              <ErrorMessage name="artistCurrentTown" component="div" className="mt-1 text-sm text-danger-600" />
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Music Culture<RequiredAsterisk />
                <InfoTooltip content="Spotify <br> Select the cultural movement or scene this song represents. Choose none of these if no
options apply." />
              </label>
              <MultiSelect
                // label="Music Culture"
                options={dspDropdownOptions.musicCulture}
                value={values.musicCulture || []}
                onChange={(value) => setFieldValue("musicCulture", value)}
                placeholder="Select Music Culture"
                maxSelections={2}
                required={true}
              />
              <ErrorMessage name="musicCulture" component="div" className="mt-1 text-sm text-danger-600" />
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Instruments<RequiredAsterisk />
                <InfoTooltip content="Spotify <br> Select all instruments featured prominently in the track for genre-specific and instrument-based
playlist placement."/>
              </label>
              <MultiSelect
                // label="Instruments"
                options={dspDropdownOptions.instruments}
                value={values.instruments || []}
                onChange={(value) => setFieldValue("instruments", value)}
                placeholder="Select Instruments"
                required={true}
              />
              <ErrorMessage name="instruments" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Song Style<RequiredAsterisk />
              <InfoTooltip content="Spotify <br> Choose the style that best describes this track's sound. Select none of these if no options
accurately represent the song.
"/>
            </label>
            <MultiSelect
              // label="Song Style"
              options={dspDropdownOptions.songStyle}
              value={values.songStyle || []}
              onChange={(value) => setFieldValue("songStyle", value)}
              placeholder="Select Style"
              maxSelections={3}
              required={true}
            />
            <ErrorMessage name="songStyle" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700"
              htmlFor="spotify-url"
            >
              Spotify URI  <RequiredAsterisk />
              <InfoTooltip content="Spotify <br> Unique track identifier required for Spotify systems.
To find: Visit the artist's Spotify profile → click three dots → Share → hold Option/Alt key →
select Copy Spotify URI
"/>
            </label>
            <input
              type="text"
              id="spotify-url"
              name="spotifyUri"
              placeholder="Enter Spotify URI"
              value={values.spotifyUri || ""}
              onChange={(e) => setFieldValue("spotifyUri", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.spotifyUri && touched.spotifyUri ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <p className="mt-1 text-xs text-neutral-500">e.g., spotify:artist:5zixe6AbgXPqt4c1uSl94L <span className="font-semibold text-neutral-700">OR</span> 5zixe6AbgXPqt4c1uSl94L</p>
            <ErrorMessage name="spotifyUri" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Spotify Playlist
              <InfoTooltip content="Spotify <br> Select up to 3 editorial playlists that match your song's genre for consideration. Only playlists matching your selected main genre(s) will appear." />
            </label>
            <MultiSelect
              options={Array.from(new Set([...(values.spotifyPlaylist || []), ...getSpotifyPlaylists()])).sort()}
              value={values.spotifyPlaylist || []}
              onChange={(value) => setFieldValue("spotifyPlaylist", value)}
              placeholder="Select up to 3 playlists"
              maxSelections={3}
              required={false}
            />
            <ErrorMessage name="spotifyPlaylist" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Notable-Specific Fields */}
          {values.pitchToNotable && (
            <div className="mt-6">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="flex flex-col">
                  <label htmlFor="notable-submission-category" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                    Submission Category <RequiredAsterisk />
                    <InfoTooltip content="Input is for Notable" />
                  </label>
                  <div className="relative">
                    <select
                      id="notable-submission-category"
                      name="notableSubmissionCategory"
                      value={values.notableSubmissionCategory || ""}
                      onChange={(e) => setFieldValue("notableSubmissionCategory", e.target.value)}
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.notableSubmissionCategory && touched.notableSubmissionCategory ? 'border-danger-500' : 'border-neutral-300'}`}
                    >
                      <option value="" disabled>Select Submission Category</option>
                      {dspDropdownOptions.notableSubmissionCategory.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="w-4 h-4 text-neutral-400">▼</span>
                    </div>
                  </div>
                  <ErrorMessage name="notableSubmissionCategory" component="div" className="mt-1 text-sm text-danger-600" />
                </div>

                <div>
                  <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                    Global Music Program Interest
                    <InfoTooltip content="Input is for Notable" />
                  </label>
                  <MultiSelect
                    options={dspDropdownOptions.notableGlobalMusicProgram}
                    value={values.notableGlobalMusicProgram || []}
                    onChange={(value) => setFieldValue("notableGlobalMusicProgram", value)}
                    placeholder="Select Global Music Program(s)"
                    required={false}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="notable-subgenre" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                  Notable Subgenre <RequiredAsterisk />
                  <InfoTooltip content="Input is for Notable" />
                </label>
                <div className="relative">
                  <select
                    id="notable-subgenre"
                    name="notableSubgenre"
                    value={values.notableSubgenre || ""}
                    onChange={(e) => setFieldValue("notableSubgenre", e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.notableSubgenre && touched.notableSubgenre ? 'border-danger-500' : 'border-neutral-300'}`}
                  >
                    <option value="" disabled>Select Notable Subgenre</option>
                    {getSubgenres("spotify").map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="w-4 h-4 text-neutral-400">▼</span>
                  </div>
                </div>
                <ErrorMessage name="notableSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
              </div>

              <div className="mt-4">
                <label htmlFor="notable-songwriter" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                  Songwriter <RequiredAsterisk />
                  <InfoTooltip content="Input is for Notable" />
                </label>
                <input
                  type="text"
                  id="notable-songwriter"
                  name="notableSongwriter"
                  placeholder="Enter songwriter name(s)"
                  value={values.notableSongwriter || ""}
                  onChange={(e) => setFieldValue("notableSongwriter", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.notableSongwriter && touched.notableSongwriter ? 'border-danger-500' : 'border-neutral-300'}`}
                />
                <ErrorMessage name="notableSongwriter" component="div" className="mt-1 text-sm text-danger-600" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Amazon Specific Fields */}
      {isDspSelected("amazon") && (
        <div className="mt-4">
          <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
            Location of Listeners <RequiredAsterisk />
            <InfoTooltip content="Amazon <br>Primary country where this artist has the strongest listener base, used for regional playlist targeting." />
          </label>
          <MultiSelect
            options={dspDropdownOptions.listenersLocation}
            value={values.listenersLocation || []}
            onChange={(value) => setFieldValue("listenersLocation", value)}
            placeholder="Select Locations"
            maxSelections={1}
            required={true}
          />

          <ErrorMessage name="listenersLocation" component="div" className="mt-1 text-sm text-danger-600" />
          <SimilarArtist
          />

          <div className="mt-4">
            <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Song Category <RequiredAsterisk />
              <InfoTooltip content="Amazon <br> Identify the primary listening context or mood when audiences would engage with this track." />
            </label>
            <MultiSelect
              options={dspDropdownOptions.songCategory}
              value={values.songCategory || []}
              onChange={(value) => setFieldValue("songCategory", value)}
              placeholder="Select Categories"
              maxSelections={3}
              required={true}

            />
            <ErrorMessage name="songCategory" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="asin"
              className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700"
            >
              ASIN
              <InfoTooltip content="Amazon <br> Amazon Standard Identification Number found in the artist's Amazon Music URL after /artists/ Example URL: amazon.com/music/player/artists/B075NQRLHS → ASIN: B075NQRLHS" />
            </label>
            <input
              type="text"
              id="asin"
              name="asin"
              placeholder="Enter ASIN"
              value={values.asin || ""}
              onChange={(e) => setFieldValue("asin", e.target.value)}
              className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {/* Deezer Specific Fields */}
      {isDspSelected("deezer") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="priority" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Priority  <RequiredAsterisk />
              <InfoTooltip content="Deezer <br> Set release priority level for editorial review. Note: Marking every release as high priority will result in all submissions being treated as regular priority" />
            </label>
            <div className="relative">
              <select
                id="priority"
                name="priority"
                value={values.priority || ""}
                onChange={(e) => setFieldValue("priority", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.priority && touched.priority ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              >
                <option value="" disabled>
                  Select Priority Level
                </option>
                {dspDropdownOptions.priority.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="priority" component="div" className="mt-1 text-sm text-danger-600" />
          </div>
          {/* Fix the Deezer Editorial Team alignment by adding mt-4 */}
          <div className="flex flex-col mt-4">
            <label htmlFor="deezer-editorial" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Deezer Editorial Team <RequiredAsterisk />
              <InfoTooltip content="Deezer <br> Select the regional editorial team that should focus on this release. Choose GLOBAL for US-based campaigns." />
            </label>
            <div className="relative">
              <select
                id="deezer-editorial"
                name="deezerEditorialTeam"
                value={values.deezerEditorialTeam || ""}
                onChange={(e) => setFieldValue("deezerEditorialTeam", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.deezerEditorialTeam && touched.deezerEditorialTeam ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              >
                <option value="" disabled>
                  Select Editorial Team
                </option>
                {renderOptions(dspDropdownOptions.deezerEditorialTeam)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="deezerEditorialTeam" component="div" className="mt-1 text-sm text-danger-600" />
          </div>
        </div>
      )}

      {/* Deezer Playlist Field */}
      {isDspSelected("deezer") && (
        <div className="mt-4">
          <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
            Deezer Playlist  
            <InfoTooltip content="Deezer <br> Select up to 3 editorial playlists that match your song's genre for consideration. Only playlists matching your selected main genre(s) will appear." />
          </label>
          <MultiSelect
            options={getDeezerPlaylists()}
            value={values.deezerPlaylist || []}
            onChange={(value) => setFieldValue("deezerPlaylist", value)}
            placeholder="Select up to 3 playlists"
            maxSelections={3}
            required={false}
          />
          <ErrorMessage name="deezerPlaylist" component="div" className="mt-1 text-sm text-danger-600" />
        </div>
      )}

      {/* TIDAL Specific Fields */}
      {isDspSelected("TIDAL") && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mb-4">
            <label htmlFor="TIDAL-page" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Link to TIDAL Artist Page  <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br>Direct URL to the artist's official TIDAL profile page." />
            </label>
            <input
              type="url"
              id="TIDAL-page"
              name="TIDALpage"
              placeholder="Ex. https://tidal.com/browse/artist/17997269?u"
              value={values.TIDALpage || ""}
              onChange={(e) => setFieldValue("TIDALpage", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.TIDALpage && touched.TIDALpage ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="TIDALpage" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mb-4">
            <label htmlFor="label-distributor" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Label/Distributor <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br> Select your label first, then distributor if label isn't listed, or choose _Indie if neither appears in
the options.
" />
            </label>
            <div className="relative">
              <select
                id="label-distributor"
                name="LabelDistributor"
                value={values.LabelDistributor || ""}
                onChange={(e) => setFieldValue("LabelDistributor", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.LabelDistributor && touched.LabelDistributor ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              >
                <option value="" disabled>
                  Select Distributor
                </option>
                {renderOptions(dspDropdownOptions.LabelDistributor)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="LabelDistributor" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4 col-span-full">
            <label htmlFor="socials-links" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Links to relevant social handles <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br> Provide URLs to active social media profiles to help editorial teams understand the artist's
online presence and engagement."/>
            </label>
            <textarea
              id="socials-links"
              name="socialsLinks"
              placeholder="Instagram, TikTok, Facebook, Twitter/X, YouTube, etc..."
              value={values.socialsLinks || ""}
              onChange={(e) => setFieldValue("socialsLinks", e.target.value)}
              maxLength={500}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 h-24 ${errors.socialsLinks && touched.socialsLinks ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="socialsLinks" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* TIDAL Artist Level */}
          <div className="flex flex-col mb-4">
            <label htmlFor="tidal-artist-level" className="block mb-2 text-sm font-medium text-neutral-700">
              Artist Level <RequiredAsterisk />
            </label>
            <div className="relative">
              <select
                id="tidal-artist-level"
                name="tidalArtistLevel"
                value={values.tidalArtistLevel || ""}
                onChange={(e) => setFieldValue("tidalArtistLevel", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.tidalArtistLevel && touched.tidalArtistLevel ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Artist Level</option>
                {dspDropdownOptions.tidalArtistLevel.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="tidalArtistLevel" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* TIDAL Priority Level */}
          <div className="flex flex-col mb-4">
            <label htmlFor="tidal-priority-level" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              TIDAL Priority Level <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br> Set the priority level for this release submission to TIDAL." />
            </label>
            <div className="relative">
              <select
                id="tidal-priority-level"
                name="tidalPriorityLevel"
                value={values.tidalPriorityLevel || ""}
                onChange={(e) => setFieldValue("tidalPriorityLevel", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.tidalPriorityLevel && touched.tidalPriorityLevel ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Priority Level</option>
                {dspDropdownOptions.tidalPriorityLevel.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="tidalPriorityLevel" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Delivered in Dolby Atmos */}
          <div className="flex flex-col mb-4">
            <label htmlFor="tidal-dolby-atmos" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Delivered in Dolby Atmos <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br> Indicate whether this release is delivered in Dolby Atmos format." />
            </label>
            <div className="relative">
              <select
                id="tidal-dolby-atmos"
                name="tidalDolbyAtmos"
                value={values.tidalDolbyAtmos || ""}
                onChange={(e) => setFieldValue("tidalDolbyAtmos", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.tidalDolbyAtmos && touched.tidalDolbyAtmos ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="tidalDolbyAtmos" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Local Market */}
          <div className="mt-4">
            <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Local Market
              <InfoTooltip content="TIDAL <br> Use this field only for local market (ex-US) releases, so they can be routed to the appropriate country editor. For US/international priority releases, please leave blank, otherwise the release will not be routed properly. TIDAL local market editors will still see the release." />
            </label>
            <MultiSelect
              options={dspDropdownOptions.tidalLocalMarket}
              value={values.tidalLocalMarket || []}
              onChange={(value) => setFieldValue("tidalLocalMarket", value)}
              placeholder="Select Local Market(s)"
              required={false}
            />
          </div>

          {/* BPM */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="tidal-bpm" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              BPM
              <InfoTooltip content="TIDAL <br> Beats per minute of the track." />
            </label>
            <input
              type="number"
              id="tidal-bpm"
              name="tidalBpm"
              placeholder="Enter BPM"
              value={values.tidalBpm || 0}
              onChange={(e) => setFieldValue("tidalBpm", Number(e.target.value))}
              className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
            />
          </div>

          {/* Confirmation of pitch submission */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="tidal-confirmation" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Do you want a confirmation of your pitch submission sent to you?
              <InfoTooltip content="TIDAL <br> If yes, TIDAL will send a confirmation email to the email address associated with your Cadence account after your submission is added to their system. This email is sent by TIDAL, not Cadence." />
            </label>
            <div className="relative">
              <select
                id="tidal-confirmation"
                name="tidalConfirmation"
                value={values.tidalConfirmation || ""}
                onChange={(e) => setFieldValue("tidalConfirmation", e.target.value)}
                className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500 appearance-none pr-10 cursor-pointer"
              >
                <option value="" disabled>Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Distributor - shared field for SoundCloud and Deezer. Hidden for
          Distributor-type companies (they ARE the distributor). Required
          only for label-type companies, the one field they need to fill. */}
      {(isDspSelected("soundcloud") || isDspSelected("deezer")) && companyTypeCategory !== "distributor" && (
        <div className="flex flex-col mt-4 mb-4">
          <label htmlFor="distributor" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
            Distributor {companyTypeCategory === "label" && <RequiredAsterisk />}
            <InfoTooltip content="Enter the name of the distribution service handling this release." />
          </label>
          <input
            type="text"
            id="distributor"
            name="distributor"
            placeholder="Enter Distributor"
            value={values.distributor || ""}
            onChange={(e) => setFieldValue("distributor", e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.distributor && touched.distributor ? 'border-danger-500' : 'border-neutral-300'
              }`}
          />
          <ErrorMessage name="distributor" component="div" className="mt-1 text-sm text-danger-600" />
        </div>
      )}

      {/* Label - shared field for SoundCloud and Deezer, reusing the
          soundCloudLabel Formik field so CSV/roster autofill have a single
          source of truth. Hidden for label-type companies. Required only
          for Distributor-type companies. When Apple is also selected, keep
          the existing SoundCloud<->Apple label sync behavior. */}
      {(isDspSelected("soundcloud") || isDspSelected("deezer")) && companyTypeCategory !== "label" && (
        <div className="flex flex-col mt-4 mb-4">
          <label htmlFor="soundcloud-label" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
            Label {companyTypeCategory === "distributor" && <RequiredAsterisk />}
            <InfoTooltip content="Enter the name of the label associated with this release." />
          </label>
          <input
            type="text"
            id="soundcloud-label"
            name="soundCloudLabel"
            placeholder="Enter Label"
            value={values.soundCloudLabel || ""}
            onChange={async (e) => {
              const value = e.target.value;
              await setFieldValue("soundCloudLabel", value);
              if (labelSelectedDSPs().includes("apple")) {
                await setFieldValue("appleLabel", value);
              }
            }}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.soundCloudLabel && touched.soundCloudLabel ? 'border-danger-500' : 'border-neutral-300'
              }`}
          />
          <ErrorMessage name="soundCloudLabel" component="div" className="mt-1 text-sm text-danger-600" />
        </div>
      )}

      {/* SoundCloud Specific Fields */}
      {isDspSelected("soundcloud") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="soundcloud-profile-url" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              SoundCloud Profile URL <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Direct link to the artist's official SoundCloud profile." />
            </label>
            <input
              type="url"
              id="soundcloud-profile-url"
              name="soundCloudProfileUrl"
              placeholder="Enter SoundCloud profile URL"
              value={values.soundCloudProfileUrl || ""}
              onChange={(e) => setFieldValue("soundCloudProfileUrl", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.soundCloudProfileUrl && touched.soundCloudProfileUrl ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="soundCloudProfileUrl" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="soundcloud-contact"
              className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700"
            >
              SoundCloud Contact <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Select your label contact or choose Indie Label Submission if you don't have a designated
contact." />
            </label>
            <div className="relative">
              <select
                id="soundcloud-contact"
                name="soundCloudContact"
                value={values.soundCloudContact || ""}
                onChange={(e) => setFieldValue("soundCloudContact", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.soundCloudContact && touched.soundCloudContact ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              >
                <option value="" disabled>
                  Select Contact
                </option>
                {renderOptions(dspDropdownOptions.soundCloudContact)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="soundCloudContact" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="soundcloud-release-stage" className="block mb-2 text-sm font-medium text-neutral-700">
              Release Stage <RequiredAsterisk />
            </label>
            <div className="relative">
              <select
                id="soundcloud-release-stage"
                name="soundCloudReleaseStage"
                value={values.soundCloudReleaseStage || ""}
                onChange={(e) => setFieldValue("soundCloudReleaseStage", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.soundCloudReleaseStage && touched.soundCloudReleaseStage ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              >
                <option value="" disabled>
                  Select Release Stage
                </option>
                {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="soundCloudReleaseStage" component="div" className="mt-1 text-sm text-danger-600" />
          </div> */}


          {/* SoundCloud Release Stage - only show when SoundCloud is selected alone */}
          {isDspSelected("soundcloud") && !isMultipleReleaseStageSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="soundcloud-release-stage" className="block mb-2 text-sm font-medium text-neutral-700">
                Release Stage <RequiredAsterisk />
              </label>
              <div className="relative">
                <select
                  id="soundcloud-release-stage"
                  name="soundCloudReleaseStage"
                  value={values.soundCloudReleaseStage || ""}
                  onChange={(e) => setFieldValue("soundCloudReleaseStage", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.soundCloudReleaseStage && touched.soundCloudReleaseStage ? 'border-danger-500' : 'border-neutral-300'}`}
                >
                  <option value="" disabled>
                    Select Release Stage
                  </option>
                  {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="soundCloudReleaseStage" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          <div className="mt-4">
            <label
              className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700"
              htmlFor="promotion-territories"
            >
              Promotion Territories <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Select the primary geographic region for promotional focus from the available territory options" />
            </label>
            <MultiSelect
              options={dspDropdownOptions.promotionTerritories}
              value={values.promotionTerritories || []}
              onChange={(value) => setFieldValue("promotionTerritories", value)}
              placeholder="Select promotion territories"
              required={true}
            />
            <ErrorMessage name="promotionTerritories" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="mt-4">
            <label
              className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700"
              htmlFor="fresh-music-friday"
            >
              Submit for Fresh Music Friday <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br>  Choose which country's Fresh Music Friday playlist you'd like to be considered for." />
            </label>
            <MultiSelect
              options={dspDropdownOptions.freshMusicFriday}
              value={values.freshMusicFriday || []}
              onChange={(value) => setFieldValue("freshMusicFriday", value)}
              placeholder="Select Fresh Music Friday options"
              required={true}
            />
            <ErrorMessage name="freshMusicFriday" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="playlist-consideration"
              className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700"
            >
              Playlist Consideration <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Select the SoundCloud editorial playlist that best matches your song's genre and style for
targeted consideration." />
            </label>
            <div className="relative">
              <select
                id="playlist-consideration"
                name="playlistConsideration"
                value={values.playlistConsideration || ""}
                onChange={(e) => setFieldValue("playlistConsideration", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.playlistConsideration && touched.playlistConsideration ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              >
                <option value="" disabled>
                  Select Playlist
                </option>
                {dspDropdownOptions.playlistConsideration.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="playlistConsideration" component="div" className="mt-1 text-sm text-danger-600" />
          </div>
        </div>
      )}

      {/* Apple Specific Fields */}
      {isDspSelected("apple") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-id" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Apple Artist ID <RequiredAsterisk />
              <InfoTooltip content="Apple Music <br> Numeric artist ID from the artist's Apple Music URL." />
            </label>
            <input
              type="number"
              id="apple-id"
              name="appleId"
              placeholder="Enter  Apple Artist ID"
              value={values.appleId || ""}
              onChange={(e) => setFieldValue("appleId", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.appleId && touched.appleId ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="appleId" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-id" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Apple Track ID
              <InfoTooltip content="Apple Music <br> Numeric track ID for the focus track on Apple Music." />
            </label>
            <input
              type="number"
              id="apple-track-id"
              name="appleTrackId"
              placeholder="Enter Apple Track ID"
              value={values.appleTrackId || ""}
              onChange={(e) => setFieldValue("appleTrackId", e.target.value)}
              className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
                
            />
            <ErrorMessage name="appleTrackId" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-album-id" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Apple Album ID <RequiredAsterisk />
              <InfoTooltip content="Apple Music <br> Numeric album ID from the release's Apple Music URL." />
            </label>
            <input
              type="number"
              id="apple-album-id"
              name="appleAlbumId"
              placeholder="Enter Apple Album ID"
              value={values.appleAlbumId || ""}
              onChange={(e) => setFieldValue("appleAlbumId", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.appleAlbumId && touched.appleAlbumId ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="appleAlbumId" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Apple Subgenre */}
          <div className="mt-4">
            <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Apple Subgenre <RequiredAsterisk />
              <InfoTooltip content="Apple Music <br> Select up to 3 subgenres that best describe the track's style." />
            </label>
            <MultiSelect
              options={getSubgenres("apple")}
              value={values.appleSubgenre || []}
              onChange={(value) => setFieldValue("appleSubgenre", value)}
              placeholder="Select up to 3 subgenres"
              maxSelections={3}
              required={true}
            />
            <ErrorMessage name="appleSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Apple Release Stage - only show when Apple is selected alone */}
          {!isMultipleReleaseStageSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="apple-release-stage" className="block mb-2 text-sm font-medium text-neutral-700">
                Release Stage
              </label>
              <div className="relative">
                <select
                  id="apple-release-stage"
                  name="appleReleaseStage"
                  value={values.appleReleaseStage || ""}
                  onChange={(e) => setFieldValue("appleReleaseStage", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.appleReleaseStage && touched.appleReleaseStage ? 'border-danger-500' : 'border-neutral-300'}`}
                >
                  <option value="" disabled>
                    Select Release Stage
                  </option>
                  {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="appleReleaseStage" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {/* Pitch From */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-pitch-from" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Pitch From <RequiredAsterisk />
              <InfoTooltip content="Apple Music <br> Select the region where the pitch originates from." />
            </label>
            <div className="relative">
              <select
                id="apple-pitch-from"
                name="applePitchFrom"
                value={values.applePitchFrom || ""}
                onChange={(e) => setFieldValue("applePitchFrom", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.applePitchFrom && touched.applePitchFrom ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Pitch From</option>
                {dspDropdownOptions.listenersLocation.map((location) => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="applePitchFrom" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Pitch To */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-pitch-to" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Pitch To <RequiredAsterisk />
              <InfoTooltip content="Apple Music <br> Select the target region for this pitch." />
            </label>
            <div className="relative">
              <select
                id="apple-pitch-to"
                name="applePitchTo"
                value={values.applePitchTo || ""}
                onChange={(e) => setFieldValue("applePitchTo", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.applePitchTo && touched.applePitchTo ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Pitch To</option>
                {dspDropdownOptions.listenersLocation.map((location) => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="applePitchTo" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Apple Label - only show when Apple is selected alone (not with SoundCloud) */}
          {!isMultipleLabelSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="apple-label" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Label
                <InfoTooltip content="Apple Music <br> Name of the label associated with this release." />
              </label>
              <input
                type="text"
                id="apple-label"
                name="appleLabel"
                placeholder="Enter Label"
                value={values.appleLabel || ""}
                onChange={(e) => setFieldValue("appleLabel", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.appleLabel && touched.appleLabel ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              <ErrorMessage name="appleLabel" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {/* Apple Promotion Date - only show when Apple is selected alone (not with YouTube) */}
          {!isMultiplePromotionDateSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="apple-promotion-date" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Promotion Date
                <InfoTooltip content="Apple Music <br> Date when promotion for this release should begin." />
              </label>
              <input
                type="date"
                id="apple-promotion-date"
                name="applePromotionDate"
                value={values.applePromotionDate || ""}
                onChange={(e) => setFieldValue("applePromotionDate", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.applePromotionDate && touched.applePromotionDate ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              <ErrorMessage name="applePromotionDate" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {/* Apple Country. TIDAL doesn't ask for the artist's country (it asks for Local Market). */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-country" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Country
              <InfoTooltip content="Apple Music <br> Artist's country for regional editorial consideration." />
            </label>
            <div className="relative">
              <select
                id="apple-country"
                name="appleCountry"
                value={values.appleCountry || ""}
                onChange={(e) => setFieldValue("appleCountry", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.appleCountry && touched.appleCountry ? 'border-red-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Country</option>
                {dspDropdownOptions.artistCountry.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="appleCountry" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          {/* Has Timed Release Dropdown */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-has-timed-release" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Has Timed Release
              <InfoTooltip content="Apple Music <br> Whether this release has a scheduled timed release on Apple Music." />
            </label>
            <div className="relative">
              <select
                id="apple-has-timed-release"
                name="appleHasTimedRelease"
                value={values.appleHasTimedRelease || ""}
                onChange={(e) => setFieldValue("appleHasTimedRelease", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.appleHasTimedRelease && touched.appleHasTimedRelease ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
          </div>

          {/* Timed Release Date - only show when Has Timed Release is Yes */}
          {values.appleHasTimedRelease === "Yes" && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="apple-timed-release-date" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Timed Release Date
                <InfoTooltip content="Apple Music <br> The scheduled date and time for the timed release." />
              </label>
              <input
                type="date"
                id="apple-timed-release-date"
                name="appleTimedReleaseDate"
                value={values.appleTimedReleaseDate || ""}
                onChange={(e) => setFieldValue("appleTimedReleaseDate", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.appleTimedReleaseDate && touched.appleTimedReleaseDate ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              <ErrorMessage name="appleTimedReleaseDate" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {/* Has Motion Artwork Dropdown */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-has-motion-artwork" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Has Motion Artwork
              <InfoTooltip content="Apple Music <br> Whether this release includes animated/motion artwork." />
            </label>
            <div className="relative">
              <select
                id="apple-has-motion-artwork"
                name="appleHasMotionArtwork"
                value={values.appleHasMotionArtwork || ""}
                onChange={(e) => setFieldValue("appleHasMotionArtwork", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.appleHasMotionArtwork && touched.appleHasMotionArtwork ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
          </div>

          {/* Has Spatial Audio Dropdown */}
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-has-spatial-audio" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Has Spatial Audio
              <InfoTooltip content="Apple Music <br> Whether this track is available in Spatial Audio (Dolby Atmos)." />
            </label>
            <div className="relative">
              <select
                id="apple-has-spatial-audio"
                name="appleHasSpatialAudio"
                value={values.appleHasSpatialAudio || ""}
                onChange={(e) => setFieldValue("appleHasSpatialAudio", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.appleHasSpatialAudio && touched.appleHasSpatialAudio ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* YouTube Specific Fields */}
      {isDspSelected("youtube") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="mt-4">
            <label
              className="block mb-2 text-sm font-medium text-neutral-700"
              htmlFor="youtube-promotion-surface"
            >
              Promotion Surface <RequiredAsterisk />
            </label>
            <MultiSelect
              options={dspDropdownOptions.youtubePromotionSurface}
              value={values.youtubePromotionSurface || []}
              onChange={(value) => setFieldValue("youtubePromotionSurface", value)}
              placeholder="Select promotion surfaces"
              required={true}
            />
            <ErrorMessage name="youtubePromotionSurface" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* YouTube Release Stage - only show when YouTube is selected alone */}
          {!isMultipleReleaseStageSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="youtube-release-stage" className="block mb-2 text-sm font-medium text-neutral-700">
                Release Stage <RequiredAsterisk />
              </label>
              <div className="relative">
                <select
                  id="youtube-release-stage"
                  name="youtubeReleaseStage"
                  value={values.youtubeReleaseStage || ""}
                  onChange={(e) => setFieldValue("youtubeReleaseStage", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.youtubeReleaseStage && touched.youtubeReleaseStage ? 'border-danger-500' : 'border-neutral-300'}`}
                >
                  <option value="" disabled>
                    Select Release Stage
                  </option>
                  {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="youtubeReleaseStage" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}


          {/* Shared Promotion Date field when multiple DSPs (YouTube, Apple) are selected */}
          {isMultiplePromotionDateSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="shared-promotion-date" className="block mb-2 text-sm font-medium text-neutral-700">
                Promotion Date <RequiredAsterisk />
              </label>
              <input
                type="date"
                id="shared-promotion-date"
                name="sharedPromotionDate"
                value={values.youtubePromotionDate || values.applePromotionDate || ""}
                onChange={async (e) => {
                  const value = e.target.value;
                  const dsps = promotionDateSelectedDSPs();
                  if (dsps.includes("youtube")) await setFieldValue("youtubePromotionDate", value);
                  if (dsps.includes("apple")) await setFieldValue("applePromotionDate", value);
                }}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${(errors.youtubePromotionDate || errors.applePromotionDate) && (touched.youtubePromotionDate || touched.applePromotionDate) ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              {(errors.youtubePromotionDate || errors.applePromotionDate) && (touched.youtubePromotionDate || touched.applePromotionDate) && (
                <div className="mt-1 text-sm text-danger-600">
                  {errors.youtubePromotionDate || errors.applePromotionDate}
                </div>
              )}
            </div>
          )}

          {/* YouTube Promotion Date - only show when YouTube is selected alone */}
          {!isMultiplePromotionDateSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label
                htmlFor="youtube-promotion-date"
                className="block mb-2 text-sm font-medium text-neutral-700"
              >
                Promotion Date <RequiredAsterisk />
              </label>
              <input
                type="date"
                id="youtube-promotion-date"
                name="youtubePromotionDate"
                value={values.youtubePromotionDate || ""}
                onChange={(e) => setFieldValue("youtubePromotionDate", e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.youtubePromotionDate && touched.youtubePromotionDate ? 'border-danger-500' : 'border-neutral-300'
                  }`}
              />
              <ErrorMessage name="youtubePromotionDate" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          <div className="mt-4">
            <label
              className="block mb-2 text-sm font-medium text-neutral-700"
              htmlFor="youtube-territories"
            >
              Territories <RequiredAsterisk />
            </label>
            <MultiSelect
              options={dspDropdownOptions.youtubeTerritories}
              value={values.youtubeTerritories || []}
              onChange={(value) => setFieldValue("youtubeTerritories", value)}
              placeholder="Select territories (max 3)"
              maxSelections={3}
              required={true}
            />
            <ErrorMessage name="youtubeTerritories" component="div" className="mt-1 text-sm text-danger-600" />
          </div>
        </div>
      )}

      {/* Shared Release Stage field when 2+ of SoundCloud, YouTube, Apple are selected */}
      {isMultipleReleaseStageSelected() && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="shared-release-stage" className="block mb-2 text-sm font-medium text-neutral-700">
              Release Stage <RequiredAsterisk />
            </label>
            <div className="relative">
              <select
                id="shared-release-stage"
                name="sharedReleaseStage"
                value={values.soundCloudReleaseStage || values.youtubeReleaseStage || values.appleReleaseStage || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const dsps = releaseStageSelectedDSPs();
                  if (dsps.includes("soundcloud")) setFieldValue("soundCloudReleaseStage", value);
                  if (dsps.includes("youtube")) setFieldValue("youtubeReleaseStage", value);
                  if (dsps.includes("apple")) setFieldValue("appleReleaseStage", value);
                }}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${(errors.soundCloudReleaseStage || errors.youtubeReleaseStage || errors.appleReleaseStage) && (touched.soundCloudReleaseStage || touched.youtubeReleaseStage || touched.appleReleaseStage) ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>
                  Select Release Stage
                </option>
                {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            {(errors.soundCloudReleaseStage || errors.youtubeReleaseStage || errors.appleReleaseStage) &&
             (touched.soundCloudReleaseStage || touched.youtubeReleaseStage || touched.appleReleaseStage) && (
              <div className="mt-1 text-sm text-danger-600">
                {errors.soundCloudReleaseStage || errors.youtubeReleaseStage || errors.appleReleaseStage}
              </div>
            )}
          </div>
        </div>
      )}

      {isDspSelected("siriusxm") && (
        <div>
          {/* Canadian Content Checkbox - Half Width */}
          <div className="grid grid-cols-1 gap-4 mt-4 lg:grid-cols-2">
            <div>
              <label
                htmlFor="canadian-content"
                className="flex min-h-[72px] items-start p-4
                  bg-white rounded-xl border border-neutral-300 shadow-sm
                  hover:shadow-md transition-all cursor-pointer gap-3"
              >
                <input
                  type="checkbox"
                  id="canadian-content"
                  name="canadianContent"
                  checked={values.canadianContent || false}
                  onChange={(e) => setFieldValue("canadianContent", e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                />
                <span className="text-sm font-medium leading-snug text-neutral-800">
                  Is this song Canadian content <span className="text-danger-600">*</span>
                </span>
              </label>
              <ErrorMessage name="canadianContent" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          </div>

          {/* Song Writers and SiriusXM Channel - Side by Side */}
          <div className="grid grid-cols-1 gap-4 mt-4 lg:grid-cols-2">
            <div className="flex flex-col">
              <label htmlFor="siriusxm-songwriters" className="block mb-2 text-sm font-medium text-neutral-700">
                Song Writers <span className="text-danger-600">*</span>
              </label>
              <input
                type="text"
                id="siriusxm-songwriters"
                name="songWriters"
                placeholder="Enter Song Writers"
                value={values.songWriters || ""}
                onChange={(e) => setFieldValue("songWriters", e.target.value)}
                className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
              />
              <ErrorMessage name="songWriters" component="div" className="mt-1 text-sm text-danger-600" />
            </div>

            <div className="flex flex-col">
              <label htmlFor="siriusxm-channel" className="block mb-2 text-sm font-medium text-neutral-700">
                SiriusXM Channel <span className="text-danger-600">*</span>
              </label>
              <div className="relative">
                <select
                  id="siriusxm-channel"
                  name="siriusXmChannel"
                  value={values.siriusXmChannel || ""}
                  onChange={(e) => setFieldValue("siriusXmChannel", e.target.value)}
                  className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500 appearance-none pr-10 cursor-pointer"
                  required
                >
                  <option value="" disabled>
                    Select SiriusXM Channel
                  </option>
                  {
                    dspDropdownOptions.siriusXmChannel.map((channel) => {
                      return (
                        <option key={channel} value={channel}>
                          {channel}
                        </option>
                      );
                    })
                  }
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="siriusXmChannel" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          </div>
        </div>
      )}

      {/* Vevo Specific Fields */}
      {isDspSelected("vevo") && (
        <div className="mt-6">
          <h3 className="mb-4 text-lg font-medium text-neutral-900">Vevo Information</h3>

          {/* Row 1: Content Type and Premiere Date */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col">
              <label htmlFor="vevo-content-type" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Content Type <RequiredAsterisk />
                <InfoTooltip content="Relevant to Vevo submissions" />
              </label>
              <div className="relative">
                <select
                  id="vevo-content-type"
                  name="vevoContentType"
                  value={values.vevoContentType || ""}
                  onChange={(e) => setFieldValue("vevoContentType", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.vevoContentType && touched.vevoContentType ? 'border-danger-500' : 'border-neutral-300'}`}
                >
                  <option value="" disabled>Select Content Type</option>
                  {dspDropdownOptions.vevoContentType.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="vevoContentType" component="div" className="mt-1 text-sm text-danger-600" />
            </div>

            <div className="flex flex-col">
              <label htmlFor="vevo-premiere-date" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Premiere Date <RequiredAsterisk />
                <InfoTooltip content="Relevant to Vevo submissions" />
              </label>
              <input
                type="date"
                id="vevo-premiere-date"
                name="vevoPremiereDate"
                value={values.vevoPremiereDate || ""}
                onChange={(e) => setFieldValue("vevoPremiereDate", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.vevoPremiereDate && touched.vevoPremiereDate ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              <ErrorMessage name="vevoPremiereDate" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          </div>

          {/* Row 2: Content Provider */}
          <div className="mt-4">
            <label htmlFor="vevo-content-provider" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Content Provider <RequiredAsterisk />
              <InfoTooltip content="Relevant to Vevo submissions" />
            </label>
            <input
              type="text"
              id="vevo-content-provider"
              name="vevoContentProvider"
              placeholder="Enter Content Provider"
              value={values.vevoContentProvider || ""}
              onChange={(e) => setFieldValue("vevoContentProvider", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.vevoContentProvider && touched.vevoContentProvider ? 'border-danger-500' : 'border-neutral-300'}`}
            />
            <ErrorMessage name="vevoContentProvider" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Row 3: Subgenre */}
          <div className="mt-4">
            <label className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Subgenre <RequiredAsterisk />
              <InfoTooltip content="Relevant to Vevo submissions" />
            </label>
            <MultiSelect
              options={getSubgenres("vevo")}
              value={values.vevoSubgenre || []}
              onChange={(value) => setFieldValue("vevoSubgenre", value)}
              placeholder="Select up to 2 subgenres"
              maxSelections={2}
              required={true}
            />
            <ErrorMessage name="vevoSubgenre" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Subgenre Other - Dynamic field when "Other" is selected */}
          {values.vevoSubgenre?.includes("Other") && (
            <div className="mt-4">
              <label htmlFor="vevo-subgenre-other" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Specify Other Subgenre <RequiredAsterisk />
                <InfoTooltip content="Relevant to Vevo submissions" />
              </label>
              <input
                type="text"
                id="vevo-subgenre-other"
                name="vevoSubgenreOther"
                placeholder="Enter subgenre"
                value={values.vevoSubgenreOther || ""}
                onChange={(e) => setFieldValue("vevoSubgenreOther", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.vevoSubgenreOther && touched.vevoSubgenreOther ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              <ErrorMessage name="vevoSubgenreOther" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {/* Row 4: Brand Affiliations, Category Restrictions, Video Product Placement, Additional Notes */}
          <div className="mt-4">
            <label htmlFor="vevo-additional-notes" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Brand Affiliations, Category Restrictions, Video Product Placement, Additional Notes <RequiredAsterisk />
              <InfoTooltip content="Relevant to Vevo submissions" />
            </label>
            <textarea
              id="vevo-additional-notes"
              name="vevoAdditionalNotes"
              placeholder="Enter brand affiliations, category restrictions, video product placement, and additional notes"
              value={values.vevoAdditionalNotes || ""}
              onChange={(e) => setFieldValue("vevoAdditionalNotes", e.target.value)}
              rows={5}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.vevoAdditionalNotes && touched.vevoAdditionalNotes ? 'border-danger-500' : 'border-neutral-300'}`}
            />
            <ErrorMessage name="vevoAdditionalNotes" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Row 8: Preview Link (Optional) */}
          <div className="mt-4">
            <label htmlFor="vevo-preview-link" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Preview Link
              <InfoTooltip content="Relevant to Vevo submissions" />
            </label>
            <input
              type="url"
              id="vevo-preview-link"
              name="vevoPreviewLink"
              placeholder="Enter preview URL"
              value={values.vevoPreviewLink || ""}
              onChange={(e) => setFieldValue("vevoPreviewLink", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.vevoPreviewLink && touched.vevoPreviewLink ? 'border-danger-500' : 'border-neutral-300'}`}
            />
            <ErrorMessage name="vevoPreviewLink" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Row 9: Vevo Rep */}
          <div className="mt-4">
            <label htmlFor="vevo-rep" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
              Vevo Rep <RequiredAsterisk />
              <InfoTooltip content="Relevant to Vevo submissions" />
            </label>
            <div className="relative">
              <select
                id="vevo-rep"
                name="vevoRep"
                value={values.vevoRep || ""}
                onChange={(e) => setFieldValue("vevoRep", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.vevoRep && touched.vevoRep ? 'border-danger-500' : 'border-neutral-300'}`}
              >
                <option value="" disabled>Select Vevo Rep</option>
                {dspDropdownOptions.vevoRep.map((rep) => (
                  <option key={rep} value={rep}>{rep}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="w-4 h-4 text-neutral-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="vevoRep" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          {/* Vevo Rep Other - Dynamic field when "Other" is selected */}
          {values.vevoRep === "Other" && (
            <div className="mt-4">
              <label htmlFor="vevo-rep-other" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Specify Vevo Rep Name <RequiredAsterisk />
                <InfoTooltip content="Relevant to Vevo submissions" />
              </label>
              <input
                type="text"
                id="vevo-rep-other"
                name="vevoRepOther"
                placeholder="Enter Vevo Rep name"
                value={values.vevoRepOther || ""}
                onChange={(e) => setFieldValue("vevoRepOther", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.vevoRepOther && touched.vevoRepOther ? 'border-danger-500' : 'border-neutral-300'}`}
              />
              <ErrorMessage name="vevoRepOther" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          )}

          {/* Row 10: CTV Only and Re-feature - Side by Side */}
          <div className="grid grid-cols-1 gap-4 mt-4 lg:grid-cols-2">
            <div className="flex flex-col">
              <label htmlFor="vevo-ctv-only" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Is this for CTV only? <RequiredAsterisk />
                <InfoTooltip content="Relevant to Vevo submissions" />
              </label>
              <div className="relative">
                <select
                  id="vevo-ctv-only"
                  name="vevoIsCtvOnly"
                  value={values.vevoIsCtvOnly || ""}
                  onChange={(e) => setFieldValue("vevoIsCtvOnly", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.vevoIsCtvOnly && touched.vevoIsCtvOnly ? 'border-danger-500' : 'border-neutral-300'}`}
                >
                  <option value="" disabled>Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="vevoIsCtvOnly" component="div" className="mt-1 text-sm text-danger-600" />
            </div>

            <div className="flex flex-col">
              <label htmlFor="vevo-refeature" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
                Is this a re-feature? <RequiredAsterisk />
                <InfoTooltip content="Relevant to Vevo submissions" />
              </label>
              <div className="relative">
                <select
                  id="vevo-refeature"
                  name="vevoIsRefeature"
                  value={values.vevoIsRefeature || ""}
                  onChange={(e) => setFieldValue("vevoIsRefeature", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${errors.vevoIsRefeature && touched.vevoIsRefeature ? 'border-danger-500' : 'border-neutral-300'}`}
                >
                  <option value="" disabled>Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="w-4 h-4 text-neutral-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="vevoIsRefeature" component="div" className="mt-1 text-sm text-danger-600" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DspSpecificInfo;